// FILE: stratify_website/src/layout/EducationMeta.jsx
import { useEffect } from "react";
import { SITE } from "../config/site";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, json) {
  const selector = `script[type="application/ld+json"][data-ld="${id}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-ld", id);
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(json);
}

function stripTrailingSlash(url) {
  return (url || "").replace(/\/+$/, "");
}

function ensureLeadingSlash(path) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function resolveCanonical({ canonical, path }) {
  // ✅ Prefer explicit canonical if provided
  if (typeof canonical === "string" && canonical.length) return canonical;

  // ✅ Prefer explicit path (critical for hash routing)
  const baseUrl = stripTrailingSlash(SITE.url || "");
  if (typeof path === "string" && path.length) {
    const p = ensureLeadingSlash(path);
    return baseUrl ? `${baseUrl}${p}` : p;
  }

  // Fallback: (hash routing will usually be "/" here)
  const currentPath = window.location.pathname || "/";
  return baseUrl ? `${baseUrl}${currentPath}` : currentPath;
}

export function Meta({
  title,
  description,
  path, // pass "/education" or "/education/my-post" for correct canonical
  canonical, // OR pass full canonical URL
  image,
  keywords,
  noindex = false,
  type = "website", // "website" | "article"
  jsonLd, // pass JSON-LD object
  article, // optional { publishedTime, modifiedTime, section, tags:[] }
} = {}) {
  useEffect(() => {
    const baseUrl = stripTrailingSlash(SITE.url || "");
    const finalCanonical = resolveCanonical({ canonical, path });

    const finalTitle = title
      ? (title.includes(SITE.name) ? title : `${title} | ${SITE.name}`)
      : SITE.name;

    const finalDescription = description || SITE.description || "";

    const finalKeywords = Array.isArray(keywords)
      ? keywords.join(", ")
      : Array.isArray(SITE.keywords)
      ? SITE.keywords.join(", ")
      : "";

    const finalImage = image
      ? (image.startsWith("http") ? image : `${baseUrl}${ensureLeadingSlash(image)}`)
      : (SITE.ogImage ? `${baseUrl}${ensureLeadingSlash(SITE.ogImage)}` : undefined);

    document.title = finalTitle;

    upsertLink("canonical", finalCanonical);

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: finalDescription,
    });

    if (finalKeywords) {
      upsertMeta('meta[name="keywords"]', {
        name: "keywords",
        content: finalKeywords,
      });
    }

    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex,nofollow" : "index,follow",
    });

    // Open Graph
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE.name,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: finalTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: finalDescription,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: finalCanonical,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    if (finalImage) {
      upsertMeta('meta[property="og:image"]', {
        property: "og:image",
        content: finalImage,
      });
    }

    // Article extras (nice SEO signal)
    if (type === "article" && article) {
      if (article.publishedTime) {
        upsertMeta('meta[property="article:published_time"]', {
          property: "article:published_time",
          content: article.publishedTime,
        });
      }
      if (article.modifiedTime) {
        upsertMeta('meta[property="article:modified_time"]', {
          property: "article:modified_time",
          content: article.modifiedTime,
        });
      }
      if (article.section) {
        upsertMeta('meta[property="article:section"]', {
          property: "article:section",
          content: article.section,
        });
      }
      if (Array.isArray(article.tags)) {
        // Remove any existing article:tag metas we created
        document.head
          .querySelectorAll('meta[property="article:tag"][data-owned="true"]')
          .forEach((n) => n.remove());

        article.tags.forEach((t) => {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.setAttribute("content", t);
          el.setAttribute("data-owned", "true");
          document.head.appendChild(el);
        });
      }
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: finalTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: finalDescription,
    });
    if (finalImage) {
      upsertMeta('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: finalImage,
      });
    }

    // JSON-LD (Article / EducationPosting / BreadcrumbList etc.)
    if (jsonLd && typeof jsonLd === "object") {
      upsertJsonLd("primary", jsonLd);
    }
  }, [title, description, path, canonical, image, keywords, noindex, type, jsonLd, article]);

  return null;
}
